import { create } from "zustand";

// Transaction modal data types
interface ITransactionModalInfo {
    from: {
        image: string;
        name: string;
    };
    to: {
        image: string;
        name: string;
    };
    seconds: number;
}

// All possible modal names in the application
export type ModalName =
    // Profile modals
    | "referal"
    | "disconnect"
    | "select"
    | "import"
    | "mobile"
    | "create"
    | "connect"
    // Transaction modals
    | "successTransaction"
    | "warningTransaction"
    | "errorTransaction"
    | "pendingTransaction"
    // Wallet modals
    | "transfer"
    | "importWallet"
    | "createWallet"
    | "connectWallet"
    | "connectNewWallet"
    // Other modals
    | "currencies"
    | "Settings"
    | "memeSwap";

interface ModalState {
    // Modal visibility states (all booleans)
    modalStates: {
        // Profile modals
        referal: boolean;
        disconnect: boolean;
        select: boolean;
        import: boolean;
        mobile: boolean;
        create: boolean;
        connect: boolean;
        memeSwap: boolean;

        // Transaction modals
        successTransaction: boolean;
        warningTransaction: boolean;
        errorTransaction: boolean;
        pendingTransaction: boolean;

        // Wallet modals
        importWallet: boolean;
        createWallet: boolean;
        connectWallet: boolean;
        connectNewWallet: boolean;
        transfer: boolean;

        // Other modals
        currencies: boolean;
        Settings: boolean;
    };

    // Transaction modal data
    transactionInfo: ITransactionModalInfo;

    // Modal actions
    openModal: (name: ModalName) => void;
    closeModal: (name: ModalName) => void;
    toggleModal: (name: ModalName) => void;
    closeAllModals: () => void;

    // Set transaction info
    setTransactionInfo: (info: ITransactionModalInfo) => void;
}

// Initial state for modal visibility
const initialModalStates = {
    // Profile modals
    referal: false,
    disconnect: false,
    select: false,
    import: false,
    mobile: false,
    create: false,
    connect: false,
    memeSwap: false,

    // Transaction modals
    successTransaction: false,
    warningTransaction: false,
    errorTransaction: false,
    pendingTransaction: false,

    // Wallet modals
    importWallet: false,
    createWallet: false,
    connectWallet: false,
    connectNewWallet: false,
    transfer: false,

    // Other modals
    currencies: false,
    Settings: false,
};

const initialTransactionInfo = {
    from: {
        image: "",
        name: "",
    },
    to: {
        image: "",
        name: "",
    },
    seconds: 0,
};

export const useModalStore = create<ModalState>()((set) => ({
    // Initialize state
    modalStates: initialModalStates,
    transactionInfo: initialTransactionInfo,

    // Open a specific modal
    openModal: (name: ModalName) =>
        set((state) => ({
            modalStates: {
                ...state.modalStates,
                [name]: true,
            },
        })),

    // Close a specific modal
    closeModal: (name: ModalName) =>
        set((state) => ({
            modalStates: {
                ...state.modalStates,
                [name]: false,
            },
        })),

    // Toggle a specific modal
    toggleModal: (name: ModalName) =>
        set((state) => ({
            modalStates: {
                ...state.modalStates,
                [name]: !state.modalStates[name],
            },
        })),

    // Close all modals
    closeAllModals: () =>
        set({
            modalStates: initialModalStates,
        }),

    // Set transaction info
    setTransactionInfo: (info: ITransactionModalInfo) => set({ transactionInfo: info }),
}));

// Helper functions to make the store easier to use with existing code
export const createModalHelpers = () => {
    const modalStore = useModalStore();

    // Create properties for each modal state
    const modalStates = Object.keys(modalStore.modalStates).reduce(
        (acc, key) => {
            acc[key as ModalName] = modalStore.modalStates[key as ModalName];
            return acc;
        },
        {} as Record<ModalName, boolean>,
    );

    return {
        ...modalStates,
        openModal: modalStore.openModal,
        closeModal: modalStore.closeModal,
        toggleModal: modalStore.toggleModal,
        closeAllModals: modalStore.closeAllModals,
        transactionInfo: modalStore.transactionInfo,
        setTransactionInfo: modalStore.setTransactionInfo,
    };
};
